import { CommonModule } from '@angular/common';
import { viewChild, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import {
  DirectionFormDto,
  type DirectionFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-form.dto';
import { BalanceSheetSourceEnum } from '@zaoblako/shared/modules/catalog/enums/balance-sheet-source.enum';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import { DirectionTypeEnum } from '@zaoblako/shared/modules/catalog/enums/direction.type.enum';
import { FinancialFlowTypeEnum } from '@zaoblako/shared/modules/catalog/enums/financial-flow-type.enum';
import type { ClassificationTypeFindInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-type-find.dto';
import type { ClassificationCategoryFindInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-category-find.dto';
import { BalanceReportFormatService } from '@zaoblako/web/core/services/finance/balance-report-format.service';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { AccountingAccountSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/accounting-account-single-select/accounting-account-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UserAndGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-and-group-multi-select/user-and-group-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { DirectionBalanceSheetSettingsListFormComponent } from '@zaoblako/web/modules/directory/dumbs/direction-balance-sheet-settings-list-form/direction-balance-sheet-settings-list-form.component';
import { BalanceSheetSourceNamePipe } from '@zaoblako/web/shared/pipes/balance-sheet-source-name.pipe';
import { DirectionKindNamePipe } from '@zaoblako/web/shared/pipes/direction-kind-name.pipe';
import { DirectionTypeNamePipe } from '@zaoblako/web/shared/pipes/direction-type-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { Observable, Subscription } from 'rxjs';
import { omit } from 'lodash';

@Component({
  standalone: true,
  selector: 'crm-direction-form',
  templateUrl: './direction-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SingleSelectComponent,
    DirectionTypeNamePipe,
    DirectionKindNamePipe,
    DirectionSingleSelectComponent,
    CheckboxModule,
    TagModule,
    FormsModule,
    ButtonComponent,
    InputNumberModule,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    BalanceSheetSourceNamePipe,
    AccountingAccountSingleSelectComponent,
    UserAndGroupMultiSelectComponent,
    DirectionBalanceSheetSettingsListFormComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DirectionFormComponent extends BaseFormComponent<DirectionFormInterface> implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public direction = <DirectionFindInterface>{};

  public dto = DirectionFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public classificationTypes: ClassificationTypeFindInterface[] = [];

  private subscription$ = new Subscription();

  public types = Object.values(DirectionTypeEnum);
  public kinds = Object.values(DirectionKindEnum);
  public priorities = Object.values(HelpdeskTaskPriorityEnum);
  public accountingSubTypes = Object.values(BalanceSheetSourceEnum).filter(
    (val) => val !== BalanceSheetSourceEnum.BALANCE_TURNOVER
  );

  public canUploadAtrium = false;

  public directionUsers = [];

  public get DirectionTypeEnum() {
    return DirectionTypeEnum;
  }

  public get DirectionKindEnum() {
    return DirectionKindEnum;
  }

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  private readonly balanceSheetSettingsForm = viewChild(DirectionBalanceSheetSettingsListFormComponent);

  public constructor(
    public readonly directionService: DirectionService,
    public readonly formStoreService: FormStoreService,
    private readonly balanceReportFormatService: BalanceReportFormatService,
    private readonly tenantService: TenantService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.tenantService
      .isConnectedToAtrium()
      .subscribe({
        next: (canUploadAtrium) => {
          this.canUploadAtrium = canUploadAtrium;
          this.cdRef.markForCheck();
        }
      });

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    this.loadClassificationTypes();
    this.subscription$.add(
      this.balanceReportFormatService.classificationTypesChanged$.subscribe(() => this.loadClassificationTypes())
    );

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private loadClassificationTypes(): void {
    this.balanceReportFormatService.findClassificationTypes().subscribe({
      next: (types) => {
        this.classificationTypes = types;
        this.cdRef.markForCheck();
      }
    });
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.directionService
      .findById<DirectionFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (direction) => {
          this.direction = direction;
          if (this.direction.users?.length) {
            this.direction.users.forEach((user) => this.directionUsers.push({ user }));
          }
          if (this.direction.userGroups?.length) {
            this.direction.userGroups.forEach((group) => this.directionUsers.push({ group }));
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveDirection() {
    const balanceSheetSettingsForm = this.balanceSheetSettingsForm();
    const balanceSheetSettingsFormValid = balanceSheetSettingsForm.validate();

    const baseForm = omit(this.direction, 'balanceSheetSettings');
    const baseFormValid = this.checkValidate(baseForm as DirectionFormInterface).valid;

    if (baseFormValid && balanceSheetSettingsFormValid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.update<DirectionFormInterface, DirectionFindInterface>(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" обновлена`);
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.directionService.create<DirectionFindInterface>(this.direction).subscribe({
          next: (direction) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Бюджетная статья создана');
            this.formStoreService.saveNext(direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить бюджетную статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Бюджетная статья "${this.direction.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreDirection() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить статью "${this.direction.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.directionService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.direction = { ...this.direction, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Статья "${this.direction.name}" восстановлена`);
            this.formStoreService.saveNext(this.direction, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public onChangeUsersAndGroup(): void {
    this.direction.users = [];
    this.direction.userGroups = [];
    this.directionUsers.forEach((directionUser) => {
      directionUser.user
        ? this.direction.users.push(directionUser.user)
        : this.direction.userGroups.push(directionUser.group);
    });
  }

  public changeKind() {
    if ([DirectionKindEnum.INCOME, DirectionKindEnum.EXPENSE].includes(this.direction.kind)) {
      if (!this.direction.classification) {
        this.direction.classification = [];
      }
    }
  }

  public getVisibleClassificationTypes(): ClassificationTypeFindInterface[] {
    const flow = this.getFlowForKind(this.direction.kind);
    if (!flow) {
      return [];
    }
    return this.classificationTypes.filter((type) => type.financialFlows?.includes(flow));
  }

  public getCategoriesForType(type: ClassificationTypeFindInterface): ClassificationCategoryFindInterface[] {
    const flow = this.getFlowForKind(this.direction.kind);
    return (type.categories || []).filter((category) => {
      if (category.name.startsWith('Доходы.')) {
        return flow === FinancialFlowTypeEnum.INCOME;
      }
      if (category.name.startsWith('Расходы.')) {
        return flow === FinancialFlowTypeEnum.EXPENSE;
      }
      return true;
    });
  }

  public getSelectedCategoryForType(typeId: number): ClassificationCategoryFindInterface {
    return (this.direction.classification || []).find((item) => item.type?.id === typeId);
  }

  public setSelectedCategoryForType(
    type: ClassificationTypeFindInterface,
    category: ClassificationCategoryFindInterface
  ): void {
    if (!this.direction.classification) {
      this.direction.classification = [];
    }
    this.direction.classification = [
      ...this.direction.classification.filter((item) => item.type?.id !== type.id),
      ...(category ? [{ ...category, type }] : [])
    ];
    this.cdRef.markForCheck();
  }

  private getFlowForKind(kind: DirectionKindEnum): FinancialFlowTypeEnum | null {
    if (kind === DirectionKindEnum.INCOME) {
      return FinancialFlowTypeEnum.INCOME;
    }
    if (kind === DirectionKindEnum.EXPENSE) {
      return FinancialFlowTypeEnum.EXPENSE;
    }
    return null;
  }
}
